// src/components/ClientSearchAutocomplete.jsx
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { digitsOnly } from "../utils/phone";
import { formatClientLabel } from "../utils/clientLabel";
import { normalizeForSearch } from "../utils/normalizeSearch";

export default function ClientSearchAutocomplete({
  value,
  onChange,
  results = [],
  onSelect,
  isOpen,
  onOpenChange,
  placeholder = "Type 3+ letters or 4+ digits...",
  inputTitle = "",
  showPhone = true,
  minCharsLetters = 3,
  minCharsDigits = 4,
  noResultsText = "No matching clients.",
  renderNoResultsAction = null,
  inputClassName =
    "w-full rounded border border-slate-300 px-4 py-3 text-base sm:text-sm shadow-sm focus:border-rose-500 focus:outline-none",
  wrapClassName = "manager-schedule-search",
  dropdownClassName,
  dropdownMaxHeight,
  usePortal = false,
  portalZIndex = 800,
  loading = false,
  loadingText = "Searching...",
  formatLabel = formatClientLabel,
  disabled = false,
  inputTestId = null,
}) {
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownMaxHeightPx, setDropdownMaxHeightPx] = useState(220);
  const [portalRect, setPortalRect] = useState(null);

  const trimmed = (value || "").trim();
  const normalizedTrimmed = normalizeForSearch(trimmed);
  const digits = digitsOnly(trimmed);
  const hasLetters = /[a-z]/.test(normalizedTrimmed);
  const isNameSearch = hasLetters && trimmed.length >= minCharsLetters;
  const isPhoneSearch = !hasLetters && digits.length >= minCharsDigits;
  const termQualifies = isNameSearch || isPhoneSearch;
  const isDropdownOpen = Boolean(isOpen && termQualifies);
  const resolvedDropdownClassName =
    dropdownClassName || "manager-schedule-search-dropdown";
  const shouldUseInlineMaxHeight =
    dropdownMaxHeight !== undefined || dropdownClassName == null;

  const setOpen = useCallback(
    (next) => {
      if (typeof onOpenChange === "function") {
        onOpenChange(next);
      }
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const handleMouseDown = (event) => {
      const wrap = wrapRef.current;
      const dropdown = dropdownRef.current;
      if (wrap && wrap.contains(event.target)) return;
      if (dropdown && dropdown.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [setOpen]);

  const recalcDropdownMaxHeight = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    if (usePortal) {
      setPortalRect({
        left: Math.round(rect.left),
        width: Math.round(rect.width),
        top: Math.round(rect.bottom + 8),
      });
    }
    const dropdownTop = rect.bottom + 8;
    const padding = 16;
    const available = window.innerHeight - dropdownTop - padding;
    const MIN_CAP = 120;
    const MAX_CAP = 260;
    let next = Math.floor(
      Math.max(0, Math.min(MAX_CAP, Math.max(MIN_CAP, available)))
    );
    if (!Number.isFinite(next)) {
      next = MIN_CAP;
    }
    setDropdownMaxHeightPx(next);
  }, [usePortal]);

  useLayoutEffect(() => {
    if (!isDropdownOpen) return;
    recalcDropdownMaxHeight();
    const handleReposition = () => recalcDropdownMaxHeight();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isDropdownOpen, recalcDropdownMaxHeight]);

  const dropdownStyle = {
    overflowY: "auto",
    ...(usePortal
      ? {
          position: "fixed",
          left: portalRect?.left ?? 0,
          width: portalRect?.width ?? 0,
          top: portalRect?.top ?? 0,
          zIndex: portalZIndex,
        }
      : {
          top: "calc(100% + 8px)",
        }),
    ...(shouldUseInlineMaxHeight
      ? { maxHeight: dropdownMaxHeight ?? dropdownMaxHeightPx }
      : {}),
  };

  const handleChange = (event) => {
    onChange(event.target.value);
    setOpen(true);
  };

  const handleFocus = () => {
    setOpen(true);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setOpen(false);
    }
  };

  const dropdownNode = isDropdownOpen ? (
    <div
      ref={dropdownRef}
      className={`${resolvedDropdownClassName}${usePortal ? "" : " absolute left-0 right-0"}`}
      style={dropdownStyle}
    >
      {loading && results.length === 0 ? (
        <div className="px-4 py-3 text-xs text-slate-500">{loadingText}</div>
      ) : results.length > 0 ? (
        results.map((client) => (
          <button
            key={client.id}
            type="button"
            onClick={() => {
              onSelect(client);
              setOpen(false);
            }}
            className="w-full text-left px-4 py-3 text-slate-800 hover:bg-slate-50"
          >
            {formatLabel(client, showPhone)}
          </button>
        ))
      ) : (
        <>
          <div className="px-4 py-3 text-xs text-slate-500">{noResultsText}</div>
          {renderNoResultsAction}
        </>
      )}
    </div>
  ) : null;

  return (
    <>
      <div ref={wrapRef} className={`${wrapClassName} relative`}>
        <input
          type="text"
          value={value ?? ""}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          title={inputTitle || undefined}
          className={inputClassName}
          disabled={disabled}
          data-testid={inputTestId || undefined}
        />
        {!usePortal && dropdownNode}
      </div>
      {usePortal && dropdownNode && typeof document !== "undefined"
        ? createPortal(dropdownNode, document.body)
        : null}
    </>
  );
}
